import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Lock, ArrowLeft, CheckCircle, Eye, EyeSlash } from "@phosphor-icons/react";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) return setErr("Password must be at least 6 characters");
    if (password !== confirm) return setErr("Passwords don't match");
    if (!token) return setErr("Missing reset token");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/auth", { replace: true }), 2000);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-bone-100" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/auth" className="inline-flex items-center gap-2 text-ink-500 hover:text-ink-900 mb-8 text-sm">
          <ArrowLeft size={16} /> Back to sign in
        </Link>
        <p className="eyebrow mb-3">Reset password</p>
        <h1 className="font-serif text-4xl leading-tight mb-8">Choose a new password</h1>

        {done ? (
          <div className="card p-6 flex items-start gap-3" data-testid="reset-success">
            <CheckCircle size={22} weight="fill" className="text-moss shrink-0 mt-0.5" />
            <div>
              <p className="font-serif text-lg leading-snug">Your password has been updated.</p>
              <p className="text-sm text-ink-500 mt-1">Redirecting you to sign in…</p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="eyebrow block mb-2">New password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-500" />
                <input
                  data-testid="reset-password-input"
                  type={showPassword ? "text" : "password"}
                  className="input pl-11 pr-11"
                  placeholder="At least 6 characters"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-ink-500 hover:text-ink-900 transition-colors"
                  data-testid="reset-password-toggle"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-2">Confirm password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-500" />
                <input
                  data-testid="reset-confirm-input"
                  type={showConfirm ? "text" : "password"}
                  className="input pl-11 pr-11"
                  placeholder="Repeat password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-ink-500 hover:text-ink-900 transition-colors"
                  data-testid="reset-confirm-toggle"
                  aria-label={showConfirm ? "Hide password" : "Show password"}
                  tabIndex={-1}
                >
                  {showConfirm ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {err && (
              <div className="text-sm text-brand bg-brand/10 border border-brand/20 rounded-md px-3 py-2" data-testid="reset-error">
                {err}
              </div>
            )}
            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit-btn">
              {loading ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
